import React from 'react';
import { Box, Typography, Chip, Paper } from '@mui/material';

var NAVY = '#173E4B';

var TYPE_COLORS = {
  base_rate: { bg: '#EFF6FF', text: '#1D4ED8' },
  adj_factor: { bg: '#F0FDF4', text: '#15803D' },
  adj_amount: { bg: '#FFF7ED', text: '#C2410C' },
  adj_factor_quantity: { bg: '#F5F3FF', text: '#6D28D9' },
  cap: { bg: '#FEF2F2', text: '#B91C1C' },
};

function typeChipStyle(t) {
  var c = TYPE_COLORS[t] || { bg: '#F3F4F6', text: '#6B7280' };
  return { height: 20, fontSize: 10, fontWeight: 600, bgcolor: c.bg, color: c.text };
}

function cellValue(cell) {
  if (cell.value_in_cents != null) return String(cell.value_in_cents);
  if (cell.value_ref) return cell.value_ref;
  if (cell.value_formula) return cell.value_formula;
  return '—';
}

// Read-only view of an AI draft's proposed rating_tables (from the draft
// payload, not yet persisted). Mirrors how the review screen shows the draft's
// rating nodes read-only. Each table: name/type/dimensions, its named segments
// (label → expression), and the cells (segment combo → value).
export default function RatingTablesReview(props) {
  var tables = props.ratingTables || [];

  if (tables.length === 0) {
    return (
      <Box
        sx={{
          border: '1px dashed #E5E7EB', borderRadius: 2, bgcolor: '#FAFAFA',
          textAlign: 'center', py: 6, px: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: '#6B7280' }}>
          This draft proposes no rating tables. Pricing is driven by the rating
          nodes' base values alone (see Benefits &amp; Rating).
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {tables.map(function(t, i) {
        return (
          <Paper key={t.guid || i} elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>
                {t.name || t.guid || `Table ${i + 1}`}
              </Typography>
              <Chip label={t.table_type} sx={typeChipStyle(t.table_type)} />
              {(t.dimensions || []).map(function(d) {
                return <Chip key={d} label={d} size="small" sx={{ height: 18, fontSize: 10, bgcolor: '#F3F4F6', color: '#6B7280' }} />;
              })}
            </Box>

            {t.description && (
              <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 1.5 }}>{t.description}</Typography>
            )}

            {/* Segments */}
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9CA3AF', mb: 0.5 }}>
              Segments
            </Typography>
            <Box sx={{ mb: 1.5 }}>
              {(t.segments || []).map(function(s, si) {
                return (
                  <Box key={s.label || si} sx={{ display: 'flex', gap: 1, py: 0.4, fontSize: 12.5 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY, minWidth: 140 }}>{s.label}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#4B5563', fontFamily: 'monospace' }}>{s.expression}</Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Cells */}
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9CA3AF', mb: 0.5 }}>
              Cells
            </Typography>
            <Box>
              {(t.cells || []).map(function(c, ci) {
                return (
                  <Box key={ci} sx={{ display: 'flex', gap: 1, py: 0.4, alignItems: 'baseline' }}>
                    <Typography sx={{ fontSize: 12.5, color: '#4B5563', minWidth: 220 }}>
                      {(c.segment_refs || []).join(' + ') || '(any)'}
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: NAVY }}>→ {cellValue(c)}</Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
